import {
  confirmLastDialog,
  onInputEnter,
  openDrawer,
} from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import { assuranceAffiliations, Patient } from "@/src/data/allTypes";
import { communes } from "@/src/data/communes";
import { USER } from "@/src/data/globalVariables";
import { nationalities } from "@/src/data/nationalities";
import { wilayas } from "@/src/data/wilayas";
import apiFetch from "@/src/utils/apiFetch";
import { getServiceId, getUserId } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import Barcode from "react-barcode";
import { handlePatientCreationError } from "./patientCreationErrorHandling";
import PatientDetector from "./patientDetector";

export function PatientIdentityForm({
  initialValue,
  onChange,
}: {
  initialValue: Patient;
  onChange: (patient: Patient) => void;
}) {
  let [patient, setPatient] = useState<Patient>(initialValue);
  const [age, setAge] = useState<number | undefined>(undefined);
  const [refs, setRefs] = useState<React.RefObject<HTMLInputElement>[]>([]);

  function onPatientChange(newValue: Patient) {
    setPatient(newValue);
    onChange(newValue);
  }

  function setRef(index: number, ref: React.RefObject<HTMLInputElement>) {
    refs[index] = ref;
    setRefs([...refs]);
  }

  function focusOnNext(index: number) {
    refs[index + 1]?.current?.click();
    refs[index + 1]?.current?.focus();
  }

  function focusOnPrevious(index: number) {
    refs[index - 1]?.current?.click();
    refs[index - 1]?.current?.focus();
  }

  function triggerMenu(index: number) {
    refs[index]?.current?.click();
  }

  function handleOnKeyUp(index: number, e: any) {
    if (e.key === "Enter") confirmLastDialog();
    if (e.key === "ArrowDown") focusOnNext(index);
    if (e.key === "ArrowUp") focusOnPrevious(index);
  }

  return (
    <div id="patient_form" className="grid c1 mc1 " style={{ gap: "40px" }}>
      {!patient.id && (
        <PatientDetector
          key={`${patient.firstName} ${patient.lastName} ${patient.birth} ${patient.nin}`}
          firstName={patient.firstName}
          lastName={patient.lastName}
          birth={patient.birth}
          nin={patient.nin}
          onSelect={onPatientChange}
        ></PatientDetector>
      )}

      {patient.nin && (
        <div className="center p5 mright10">
          <Barcode
            value={
              patient.nin ||
              patient.id?.replace("patients:", "")?.toString()?.slice(0, 8) ||
              ""
            }
            displayValue={false}
            height={30}
            width={1}
            fontSize={12}
            margin={0}
          ></Barcode>
          <div className=" p5  br center">
            <div className="black mtop5  ">NIS</div>
            <div className=" bold greenish">{patient.nin}</div>
          </div>
        </div>
      )}
      {/* 
      <Input
        name="N° d'identification de santé (NIS/NIN)"
        type="text"
        value={patient.nin}
        onChange={(newValue) => {
          onPatientChange({ ...patient, nin: newValue });
        }}
      ></Input> */}

      <div className="c4 mc1 gap10">
        <Input
          onRefSet={(ref) => setRef(0, ref)}
          onKeyUp={(e) => handleOnKeyUp(0, e)}
          onEnter={onInputEnter}
          name="Nom"
          type="text"
          isRequired={true}
          shouldFocus={true}
          value={patient.lastName}
          onChange={(newValue) => {
            onPatientChange({ ...patient, lastName: newValue });
          }}
        ></Input>

        <Input
          onRefSet={(ref) => setRef(1, ref)}
          onKeyUp={(e) => handleOnKeyUp(1, e)}
          onEnter={onInputEnter}
          name="Prénom"
          type="text"
          isRequired={true}
          value={patient.firstName}
          onChange={(newValue) => {
            onPatientChange({ ...patient, firstName: newValue });
          }}
        ></Input>
        <Input
          onRefSet={(ref) => setRef(2, ref)}
          onKeyUp={(e) => handleOnKeyUp(2, e)}
          onEnter={onInputEnter}
          name="Nom de jeune fille (optionel)"
          type="text"
          value={patient.maidenName}
          onChange={(newValue) => {
            onPatientChange({ ...patient, maidenName: newValue });
          }}
        ></Input>

        <Input
          onRefSet={(ref) => setRef(3, ref)}
          onKeyUp={(e) => handleOnKeyUp(3, e)}
          onEnter={onInputEnter}
          key={`${age} age`}
          name="Age (années)"
          type="number"
          value={
            age || patient.birth
              ? moment().diff(moment(patient.birth), "years")
              : undefined
          }
          onBlur={(newValue) => {
            setAge(newValue);
            onPatientChange({
              ...patient,
              birth: moment().subtract(newValue, "years").format("YYYY-01-01"),
            });
          }}
          onChange={(newValue) => {}}
        ></Input>
        <Input
          onRefSet={(ref) => setRef(4, ref)}
          onKeyUp={(e) => handleOnKeyUp(4, e)}
          onEnter={onInputEnter}
          key={`${patient.birth} birth`}
          name="Date de naissance"
          type="date"
          isRequired={true}
          value={
            patient.birth ? moment(patient.birth).format("YYYY-MM-DD") : null
          }
          onBlur={(newValue) => {
            onPatientChange({
              ...patient,
              birth: !moment(newValue).isValid()
                ? ""
                : moment(newValue).format("YYYY-MM-DD"),
            });
            setAge(moment().diff(moment(newValue), "years"));
          }}
          onChange={(newValue) => {}}
        ></Input>
        <Input
          onRefSet={(ref) => setRef(5, ref)}
          onKeyUp={(e) => handleOnKeyUp(5, e)}
          onEnter={onInputEnter}
          name="Sexe"
          isRequired={true}
          type="select"
          options={[
            {
              value: "homme",
              label: "Homme",
            },
            {
              value: "femme",
              label: "Femme",
            },
          ]}
          value={patient.sex}
          onChange={(newValue) => {
            onPatientChange({ ...patient, sex: newValue });
          }}
        ></Input>

        <Input
          onRefSet={(ref) => setRef(6, ref)}
          onKeyUp={(e) => handleOnKeyUp(6, e)}
          onEnter={onInputEnter}
          isRequired={true}
          name="N° Tel"
          type="text"
          value={patient.phoneNumber}
          onChange={(newValue) => {
            onPatientChange({ ...patient, phoneNumber: newValue });
          }}
        ></Input>

        <Input
          onRefSet={(ref) => setRef(7, ref)}
          onKeyUp={(e) => handleOnKeyUp(7, e)}
          onEnter={onInputEnter}
          name="N° Tel 2"
          type="text"
          value={patient.secondaryPhoneNumber}
          onChange={(newValue) => {
            onPatientChange({ ...patient, secondaryPhoneNumber: newValue });
          }}
        ></Input>
      </div>

      <div className="c1 gap20">
        <h3 className="mtop30">Adresse</h3>

        <div className="c4 mc1 gap10">
          <Input
            name="Wilaya"
            type="select"
            isRequired={true}
            value={patient.wilaya}
            onEnter={onInputEnter}
            options={wilayas.map((wilaya) => {
              return { value: wilaya, label: wilaya };
            })}
            onChange={(newValue) => {
              onPatientChange({ ...patient, wilaya: newValue });
            }}
          ></Input>

          <Input
            key={`${patient.wilaya} commune`}
            name="Commune"
            type="select"
            value={patient.commune}
            onEnter={onInputEnter}
            options={
              communes[
                (patient.wilaya || "Alger") as keyof typeof communes
              ]?.map((commune) => {
                return { value: commune, label: commune };
              }) || []
            }
            onChange={(newValue) => {
              onPatientChange({ ...patient, commune: newValue });
            }}
          ></Input>

          <Input
            onRefSet={(ref) => setRef(8, ref)}
            onKeyUp={(e) => handleOnKeyUp(8, e)}
            onEnter={onInputEnter}
            name="Nationalité"
            type="select"
            value={patient.nationality}
            isRequired={USER.get()?.speciality?.name?.includes("infantile")}
            options={nationalities.map((nationality) => {
              return { value: nationality, label: nationality };
            })}
            onChange={(newValue) => {
              onPatientChange({ ...patient, nationality: newValue });
            }}
          ></Input>
          <Input
            name="Adresse complète (optionel)"
            type="text"
            value={patient.address}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, address: newValue });
            }}
          ></Input>
        </div>
      </div>

      <div className="c1 gap20">
        <h3 className="mtop20">Informations supplémentaires</h3>
        <div className="c4 mc1 gap10">
          <Input
            name="Groupage"
            type="select"
            options={[
              { value: "A+", label: "A+" },
              { value: "A-", label: "A-" },
              { value: "B+", label: "B+" },
              { value: "B-", label: "B-" },
              { value: "AB+", label: "AB+" },
              { value: "AB-", label: "AB-" },
              { value: "O+", label: "O+" },
              { value: "O-", label: "O-" },
            ]}
            value={patient.bloodType}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, bloodType: newValue });
            }}
          ></Input>

          <Input
            name="Décès"
            type="select"
            options={[
              {
                value: "vivant",
                label: "Vivant",
              },
              {
                value: "décédé",
                label: "Décédé",
              },
            ]}
            value={patient.deathStatus}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, deathStatus: newValue });
            }}
          ></Input>

          <Input
            name="Date de décès"
            type="date"
            value={patient.deathDate}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, deathDate: newValue });
            }}
          ></Input>

          <Input
            name="N° du dossier papier (optionel)"
            type="text"
            value={patient.recordId}
            onChange={(newValue) => {
              onPatientChange({ ...patient, recordId: newValue });
            }}
          ></Input>

          {/* 
        <Input
          name="Profession"
          type="text"
          value={patient.profession}
          onEnter={onInputEnter}
          onChange={(newValue) => {
            onPatientChange({ ...patient, profession: newValue });
          }}
        ></Input> */}
          <Input
            name="Affiliation"
            type="select"
            options={Object.values(assuranceAffiliations)}
            value={patient.assuranceAffiliation}
            onChange={(newValue) => {
              onPatientChange({ ...patient, assuranceAffiliation: newValue });
            }}
          ></Input>
          <Input
            name="N° sécurité sociale"
            type="text"
            value={patient.socialSecurityNumber}
            onChange={(newValue) => {
              onPatientChange({ ...patient, socialSecurityNumber: newValue });
            }}
          ></Input>
          <Input
            name="اللقب"
            type="text"
            value={patient.lastNameAr}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, lastNameAr: newValue });
            }}
          ></Input>
          <Input
            name="الإسم"
            type="text"
            value={patient.firstNameAr}
            onEnter={onInputEnter}
            onChange={(newValue) => {
              onPatientChange({ ...patient, firstNameAr: newValue });
            }}
          ></Input>
        </div>
      </div>

      <div className="c1 gap20">
        <h3 className="mtop20">Famille / Personne de confiance</h3>
        <div className="c4 mc1 gap10">
          <Input
            name="Nom du père"
            type="text"
            value={patient.fatherName}
            onChange={(newValue) => {
              onPatientChange({ ...patient, fatherName: newValue });
            }}
          ></Input>
          <Input
            name="Nom de la mère"
            type="text"
            value={patient.motherName}
            onChange={(newValue) => {
              onPatientChange({ ...patient, motherName: newValue });
            }}
          ></Input>

          <Input
            name="Nom du conjoint"
            type="text"
            value={patient.spouseName}
            onChange={(newValue) => {
              onPatientChange({ ...patient, spouseName: newValue });
            }}
          ></Input>

          <Input
            name="Nombre d'enfants"
            type="select"
            options={Array.from({ length: 15 }, (_, i) => ({
              value: (i + 1).toString(),
              label: (i + 1).toString(),
            }))}
            value={patient.childrenCount}
            onChange={(newValue) => {
              onPatientChange({ ...patient, childrenCount: newValue });
            }}
          ></Input>
          <Input
            name="Nom et prénom (PDC)"
            type="text"
            value={patient.familyMemberFullName}
            onChange={(newValue) => {
              onPatientChange({ ...patient, familyMemberFullName: newValue });
            }}
          ></Input>
          <Input
            name="N° tel (PDC)"
            type="text"
            value={patient.familyMemberPhoneNumber}
            onChange={(newValue) => {
              onPatientChange({
                ...patient,
                familyMemberPhoneNumber: newValue,
              });
            }}
          ></Input>
          <Input
            name="Adresse (PDC)"
            type="text"
            value={patient.familyMemberAddress}
            onChange={(newValue) => {
              onPatientChange({ ...patient, familyMemberAddress: newValue });
            }}
          ></Input>
        </div>

        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
      </div>
    </div>
  );
}

export async function openPatientIdentityDrawer({
  patient,
  onChange,
}: {
  patient: Patient;
  onChange: (patient: Patient) => void;
}) {
  await openDrawer({
    id: "patientDrawer",
    title: "Patient",
    additionalClasses: ["big", "whiteBody"],
    content: (
      <PatientIdentityForm
        initialValue={patient}
        onChange={(newValue) => {
          patient = newValue;
        }}
      />
    ),
    onConfirm: async (e) => {
      try {
        let result = await apiFetch(
          "patients",
          "addOne",
          { patient, serviceId: getServiceId(), userId: getUserId() },
          "Erreur lors de la modification du patient"
        );
        if (!result) return;
        patient = result;
        onChange?.(patient);
      } catch (e: any) {
        await handlePatientCreationError({
          patient,
          errorMessage: e.message,
          onSelect: (patient) => {
            onChange?.(patient);
          },
        });
      }
    },
  });
}
